import React, { useRef, createContext, useContext } from "react";
import GoogleRecaptchaManager from "./GoogleRecaptchaManager";
import {
  validateSiteKey,
  type GoogleRecaptchaProviderProps,
  type GoogleRecaptchaContextValue,
  type GoogleRecaptchaManagerRef,
} from "./types";

const GoogleRecaptchaContext =
  createContext<
    | {
        ref: React.RefObject<GoogleRecaptchaManagerRef | null>;
        headerName: string;
      }
    | null
  >(null);

export function GoogleRecaptchaProvider({
  siteKey,
  headerName = "X-Recaptcha-Token",
  children,
}: GoogleRecaptchaProviderProps) {
  validateSiteKey(siteKey);

  const recaptchaRef = useRef<GoogleRecaptchaManagerRef>(null);

  return (
    <GoogleRecaptchaContext.Provider value={{ ref: recaptchaRef, headerName }}>
      <GoogleRecaptchaManager ref={recaptchaRef} siteKey={siteKey} />
      {children}
    </GoogleRecaptchaContext.Provider>
  );
}

export function useGoogleRecaptcha(): GoogleRecaptchaContextValue {
  const context = useContext(GoogleRecaptchaContext);

  if (!context) {
    throw new Error(
      "useGoogleRecaptcha must be used within GoogleRecaptchaProvider"
    );
  }

  const getToken = async (): Promise<string> => {
    if (!context.ref.current) {
      throw new Error("Google reCAPTCHA not initialized");
    }
    return await context.ref.current.execute();
  };

  const reset = (): void => {
    context.ref.current?.reset();
  };

  return {
    getToken,
    reset,
    headerName: context.headerName,
  };
}
